/**
 * Typed client for GET /admin-services/code-signing
 *
 * The controller uses utils.handleSuccess(res, 200, data) which emits:
 *   { success: true, data: <CodeSigningData> }
 * client.get() returns { data: <HTTP body> }, so the full chain is
 * response.data.data. We peel it with unwrap() from @/api/client.
 *
 * NOTE: This endpoint is not yet wired in
 * app/controllers/admin-services.js / app/routes/admin-services.js.
 * The screen will surface an error state (via react-query's isError)
 * until it ships. The shape below mirrors the JSDoc comment in the
 * source view verbatim.
 */
import { client, unwrap } from './client';

// ─── Payload shapes ───────────────────────────────────────────────────────

export interface CertStatus {
  /** e.g. "Sectigo EV" */
  issuer?: string;
  /** e.g. "O=NUREV, CN=..." */
  subject?: string;
  validFrom?: string;
  validTo?: string;
  /** SHA-256 cert fingerprint */
  fingerprint?: string;
  /** freeform cert lifecycle status */
  status?: string;
}

export interface LatestBuild {
  id?: string;
  version?: string;
  signedAt?: string;
  /** Presigned URL to the MSI */
  msi?: string;
  sha256?: string;
}

export interface VirusTotalData {
  /** Number of flagging vendors */
  detections?: number;
  /** Total vendors scanned */
  total?: number;
  lastScanAt?: string;
  /** Direct link to the VirusTotal report */
  link?: string;
}

export interface VendorVerdict {
  name: string;
  /** "clean" | "undetected" | "pass" → ok; anything else → flagged */
  verdict?: string;
  lastChecked?: string;
  /** Freeform FP escalation note */
  escalation?: string;
}

export interface CodeSigningData {
  cert?: CertStatus;
  latestBuild?: LatestBuild;
  virusTotal?: VirusTotalData;
  vendors?: VendorVerdict[];
  pendingSubmissions?: unknown[];
}

// ─── Fetch helper ─────────────────────────────────────────────────────────

export async function fetchCodeSigning(): Promise<CodeSigningData> {
  return unwrap<CodeSigningData>(
    client.get<{ data: CodeSigningData }>('/admin-services/code-signing')
  );
}
